<template>
  <div>
    <DropdownMenu v-if="actions.length > 0">
      <DropdownMenuTrigger as-child>
        <button
          class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-xs tw-text-secondary-500 hover:tw-text-white tw-bg-white tw-border tw-border-solid tw-border-secondary-600 tw-rounded-md hover:tw-bg-secondary-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-secondary-500 focus:tw-border-secondary-500"
          type="button"
        >
          <div class="tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-xs tw-font-light">
              {{ t('common.actions.bulk_actions') }}
            </span>
            <DotsVerticalIcon class="tw-w-4 tw-h-4" />
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent class="tw-w-56 !tw-border-none !tw-shadow-[0px_0px_19px_0px_#f5f5f5]">
        <DropdownMenuItem
          v-for="action in actions"
          :key="action.label"
          @click="action.onClick"
          class="tw-cursor-pointer hover:!tw-bg-gray-100 tw-transition-colors tw-group"
        >
          <div class="tw-flex tw-items-center tw-gap-2">
            <component
              :is="action.icon"
              class="tw-w-4 tw-h-4"
              :class="action.iconClass"
              v-if="action.icon"
            />
            <span>{{ action.label }}</span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <!-- Import Modal -->
    <GenericModal v-model="showImportModal" :title="t('suppliers.titles.bulk_import')">
      <template #body>
        <div class="tw-mb-4">
          <label
            for="uploadSuppliersFile"
            class="tw-cursor-pointer tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm tw-bg-white tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors"
            :class="{
              'tw-opacity-50 tw-cursor-not-allowed': isUploading,
            }"
          >
            <svg
              class="tw-w-5 tw-h-5 tw-mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            {{ t('suppliers.titles.upload_suppliers_as') }} CSV
            {{ t('common.data.or_separator') }} XLSX
          </label>
          <input
            type="file"
            id="uploadSuppliersFile"
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            class="tw-hidden"
            @change="onSupplierFileUpload"
            :disabled="isUploading"
          />
        </div>

        <div v-if="isUploading" class="tw-mb-4 tw-flex tw-items-center tw-gap-2">
          <div
            class="tw-animate-spin tw-w-4 tw-h-4 tw-border-2 tw-border-secondary-500 tw-border-t-transparent tw-rounded-full"
          ></div>
          <span class="tw-text-sm tw-text-gray-600">{{ t('common.data.uploading') }}...</span>
        </div>

        <div
          v-if="fileErrors"
          class="tw-mb-4 tw-p-3 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-md"
        >
          <p class="tw-text-sm tw-text-red-600">
            {{ t('suppliers.titles.file_has_errors') }}
            <a :href="fileErrors" download class="tw-underline tw-font-medium">
              {{ t('common.actions.download_errors') }}
            </a>
          </p>
        </div>

        <div class="tw-text-sm tw-text-gray-600">
          <p>
            {{ t('suppliers.titles.download_template') }}:
            <a :href="csvTemplate" download class="tw-text-secondary-500 tw-underline tw-mx-1">
              CSV
            </a>
            {{ t('common.data.or_separator') }}
            <a :href="xlsxTemplate" download class="tw-text-secondary-500 tw-underline tw-mx-1">
              XLSX
            </a>
          </p>
        </div>
      </template>

      <template #footer>
        <div class="tw-flex tw-justify-end tw-gap-2">
          <button
            @click="closeImportModal"
            class="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50 tw-transition-colors"
          >
            {{ t('common.forms.cancel') }}
          </button>
        </div>
      </template>
    </GenericModal>
  </div>
</template>

<script setup lang="ts">
import { ref, shallowRef, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { SupplierInList } from '@/_helpers/interfaces/suppliers/supplier-in-list.interface';
import { GenericModal } from '@/components/shared';
import { useSupplierStore } from '@/stores/supplier.store';
import { useApiUtils } from '@/_helpers/composables/api-utils.composable';
import { useMasterDataFileValidation } from '@/_helpers/composables/master-data/file-validation.composable';

import DotsVerticalIcon from '@/assets/icons/DotsVertical.svg';
import CsvIcon from '@/assets/icons/CsvIcon.svg';
import ExcelIcon from '@/assets/icons/ExcelIcon.svg'; // For XLSX files
import DocumentArrowUpIcon from '@/assets/icons/FileExport.svg'; // Using FileExport as placeholder for upload

const { t } = useI18n();
const { handleApiError } = useApiUtils();
const { validateTypeAndExtension } = useMasterDataFileValidation();
const { exportSuppliersList, getImportSuppliersTemplates, uploadSuppliersFile } =
  useSupplierStore();

const props = defineProps<{
  selectedSuppliers: SupplierInList[];
  customerId: number;
}>();

const emit = defineEmits<{
  reloadTable: [];
  clearSelection: [];
}>();

// Modal states
const showImportModal = ref(false);
const isUploading = ref(false);
const fileErrors = ref('');

// Templates
const csvTemplate = ref('');
const xlsxTemplate = ref('');

// Action handlers
const openImportModal = () => {
  showImportModal.value = true;
  fileErrors.value = '';
};

const closeImportModal = () => {
  showImportModal.value = false;
  fileErrors.value = '';
};

const onSupplierFileUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (!file) return;

  if (validateTypeAndExtension(file)) {
    try {
      fileErrors.value = '';
      isUploading.value = true;

      const uploadingResult = await uploadSuppliersFile(file, props.customerId.toString());
      isUploading.value = false;

      if (uploadingResult.success) {
        await handleApiError(async () => {
          emit('reloadTable');
          closeImportModal();
        }, t('suppliers.titles.suppliers_are_imported_successfully'));
      } else {
        fileErrors.value = uploadingResult.file;
      }
    } catch (error) {
      isUploading.value = false;
      console.error('Upload error:', error);
    }
  }

  // Reset file input
  target.value = '';
};

const exportSuppliersCSV = async () => {
  await handleApiError(async () => {
    const fileUrl = await exportSuppliersList('csv', {
      customer: props.customerId.toString(),
    });

    const link = document.createElement('a');
    link.href = fileUrl;
    link.click();
  }, t('suppliers.titles.all_suppliers_are_exported_successfully'));
};

const exportSuppliersXLSX = async () => {
  await handleApiError(async () => {
    const fileUrl = await exportSuppliersList('xlsx', {
      customer: props.customerId.toString(),
    });

    const link = document.createElement('a');
    link.href = fileUrl;
    link.click();
  }, t('suppliers.titles.all_suppliers_are_exported_successfully'));
};

// Action definitions
enum ActionType {
  IMPORT = 'import',
  EXPORT_CSV = 'export_csv',
  EXPORT_XLSX = 'export_xlsx',
}

const actions = shallowRef<any[]>([
  {
    label: t('common.actions.bulk_import'),
    icon: DocumentArrowUpIcon,
    iconClass: 'tw-text-blue-500',
    onClick: openImportModal,
    actionType: ActionType.IMPORT,
  },
  {
    label: t('common.actions.download_as') + ' csv',
    icon: CsvIcon,
    iconClass: 'tw-text-green-500',
    onClick: exportSuppliersCSV,
    actionType: ActionType.EXPORT_CSV,
  },
  {
    label: t('common.actions.download_as') + ' xlsx',
    icon: ExcelIcon,
    iconClass: 'tw-text-purple-500',
    onClick: exportSuppliersXLSX,
    actionType: ActionType.EXPORT_XLSX,
  },
]);

// Initialize templates on mount
onMounted(async () => {
  try {
    const templates = await getImportSuppliersTemplates();
    csvTemplate.value = templates.csv_template;
    xlsxTemplate.value = templates.xlsx_template;
  } catch (error) {
    console.error('Failed to load templates:', error);
  }
});
</script>
